import {controller, target, targets, attr} from '@github/catalyst'
import {reportError} from '@github-ui/failbot'
import {toggleToast} from '@github-ui/toggle-toast'
import {fetchSafeDocumentFragment} from '@github-ui/fetch-utils'
import {getBaseFetchHeaders} from '@github-ui/fetch-headers'

export interface IFeedFilterValue {
  Announcements: boolean
  Releases: boolean
  Sponsors: boolean
  Stars: boolean
  Repositories: boolean
  Follows: boolean
  Recommendations: boolean
}

@controller('feed-filter')
export class FeedFilterElement extends HTMLElement {
  @attr declare value: string
  @attr declare default: string
  @attr declare org: string
  @target declare csrfToken: HTMLInputElement
  @target declare applyButton: HTMLButtonElement
  @targets declare inputs: HTMLInputElement[]

  filterCache: Map<string, string> = new Map()
  declare initialFilterValue: IFeedFilterValue

  get filterValues(): IFeedFilterValue {
    return JSON.parse(this.value)
  }

  get defaultFilterValues(): IFeedFilterValue {
    return this.default ? JSON.parse(this.default) : this.initialFilterValue
  }

  connectedCallback() {
    this.initialFilterValue = this.filterValues
  }

  handleToggle(e: Event) {
    if (!(e.target instanceof HTMLInputElement)) {
      e.preventDefault()
    }

    const item = e.currentTarget as HTMLElement
    // capturing input elements for label and toggle-switch
    const input = item.querySelector('input') || (item as HTMLInputElement)
    const filter = this.filterValues
    const filterName = item.getAttribute('data-name') as keyof IFeedFilterValue

    if (filter[filterName] === undefined) return
    filter[filterName] = !filter[filterName]

    input.checked = filter[filterName]
    input.toggleAttribute('checked', filter[filterName])

    this.setFilterValue(filter)
    this.updateApplyButtonHydroAttributes(filter)
  }

  async handleApply() {
    let CONDUIT_FILTER_FEED_URL = '/conduit/filter'
    const activeTopic = this.getActiveTopic()

    if (this.isTopicFeed(activeTopic)) {
      CONDUIT_FILTER_FEED_URL = `${CONDUIT_FILTER_FEED_URL}?is_topic=true&topic=${activeTopic}`
    } else if (this.org) {
      CONDUIT_FILTER_FEED_URL = `${CONDUIT_FILTER_FEED_URL}?org=${this.org}`
    }

    try {
      const response = await fetch(CONDUIT_FILTER_FEED_URL, {
        method: 'PUT',
        body: JSON.stringify(this.filterValues),
        credentials: 'same-origin',
        headers: {
          'Scoped-CSRF-Token': this.csrfToken.value,
          ...getBaseFetchHeaders(),
        },
      })

      const html = await response.text()

      this.dispatchEvent(
        new CustomEvent('feed-filter:apply', {
          bubbles: true,
          detail: {
            updatedFeedContent: html,
          },
        }),
      )
    } catch (err: unknown) {
      reportError(err)
      toggleToast(document.querySelector('.js-feed-filter-error-toast')!.innerHTML)
      this.resetFilterValue()
    }
  }

  setFilterValue(value: IFeedFilterValue) {
    this.value = JSON.stringify(value)
  }

  private resetFilterValue() {
    for (const input of this.inputs) {
      const groupName = (input.name || input.getAttribute('data-name')) as keyof IFeedFilterValue

      const value = this.initialFilterValue[groupName]
      input.toggleAttribute('checked', value)
    }

    this.setFilterValue(this.initialFilterValue)
  }

  resetFilterToDefault() {
    let filter: IFeedFilterValue
    try {
      filter = this.defaultFilterValues
    } catch (error) {
      reportError(error)
      filter = this.initialFilterValue
    }

    for (const input of this.inputs) {
      const name = (input.name || input.getAttribute('name')) as keyof IFeedFilterValue
      switch (input.type) {
        case 'checkbox':
          input.checked = filter[name]
          input.toggleAttribute('checked', filter[name])
          break
        default:
          reportError(new Error(`Unexpected input type in resetFilterToDefault: ${input.type}`))
          return
      }
    }

    this.setFilterValue(filter)
    this.updateApplyButtonHydroAttributes(filter)
  }

  private reloadFilterValues(cachedContent: string) {
    this.value = cachedContent
    const filter = this.filterValues
    for (const input of this.inputs) {
      const name = input.getAttribute('name') as keyof IFeedFilterValue
      switch (input.tagName) {
        case 'INPUT':
          input.checked = filter[name]
          break
        default:
          throw new Error(`Unexpected tag name: ${input.tagName}`)
      }
    }

    this.setFilterValue(filter)
  }

  private updateApplyButtonHydroAttributes(filter: IFeedFilterValue) {
    const attrs = this.applyButton.getAttribute('data-hydro-click') as string
    const attrsObj = JSON.parse(attrs)
    attrsObj.payload.metadata.filter_groups = Object.entries(filter)
      .filter((kv: [string, unknown]) => kv[1])
      .map((kv: [string, unknown]) => kv[0])
    this.applyButton.setAttribute('data-hydro-click', JSON.stringify(attrsObj))
  }

  private getUrl() {
    return new URL(window.location.href, window.location.origin)
  }

  private getUrlParams() {
    const url = this.getUrl()
    const params = new URLSearchParams(url.search)
    return params
  }

  private isTopicFeed(topic: string) {
    return topic !== 'for-you' && !this.org
  }

  async setFeedFilterSettings(previousTopic: string | null = null) {
    let CONDUIT_FILTER_FEED_URL = '/conduit/filter'

    if (previousTopic) {
      this.cacheFilter(previousTopic)
    }

    const activeTopic = this.getActiveTopic()
    const cachedContent = this.filterCache.get(this.getFilterCacheKey(activeTopic))

    if (cachedContent) {
      this.reloadFilterValues(cachedContent)
    } else {
      if (this.isTopicFeed(activeTopic)) {
        CONDUIT_FILTER_FEED_URL = `${CONDUIT_FILTER_FEED_URL}?is_topic=true&topic=${activeTopic}`
      } else if (this.org) {
        CONDUIT_FILTER_FEED_URL = `${CONDUIT_FILTER_FEED_URL}?org=${this.org}`
      }

      const fragment = await fetchSafeDocumentFragment(document, CONDUIT_FILTER_FEED_URL)
      this.innerHTML = fragment.querySelector(this.tagName)!.innerHTML
      this.value = fragment.querySelector(this.tagName)!.getAttribute('data-value')!
    }
  }

  private getActiveTopic() {
    if (this.org) {
      return this.org
    }

    const topic = this.getUrlParams().get('topic')
    return topic || 'for-you'
  }

  cacheFilter(topic: string) {
    this.filterCache.set(this.getFilterCacheKey(topic), this.value)
  }

  private getFilterCacheKey(topic: string) {
    switch (topic) {
      case 'for-you':
        return 'for-you'
      case 'topic':
        return 'topic'
      default:
        return topic
    }
  }
}
